import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../services/api';
import { hasAuthData } from '../utils/authHeaders';

/**
 * Hook для отслеживания количества новых активированных рефералов
 */
export const useReferralNotifications = () => {
  const [unviewedCount, setUnviewedCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Загрузка счётчика при монтировании компонента
  const fetchUnviewedCount = useCallback(async () => {
    // Не делаем запрос если пользователь не авторизован
    if (!hasAuthData()) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await apiClient.user.getUnviewedReferralsCount();
      if (response.success && response.data) {
        setUnviewedCount(response.data.count);
      }
    } catch (error) {
      // Тихо игнорируем ошибки
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Пометка всех рефералов как просмотренных
  const markAsViewed = useCallback(async () => {
    // Не делаем запрос если пользователь не авторизован
    if (!hasAuthData()) {
      return;
    }
    
    try {
      await apiClient.user.markReferralsAsViewed();
      setUnviewedCount(0);
    } catch (error) {
      // Тихо игнорируем ошибки
    }
  }, []);

  useEffect(() => {
    // Загружаем счётчик при монтировании
    fetchUnviewedCount();

    // Обновляем счётчик каждые 15 секунд (уменьшили с 30)
    const interval = setInterval(fetchUnviewedCount, 15000);

    // Обновляем счётчик при возврате на вкладку (когда пользователь возвращается в приложение)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchUnviewedCount();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Обновляем счётчик при focus на окне
    const handleFocus = () => {
      fetchUnviewedCount();
    };
    
    window.addEventListener('focus', handleFocus);

    // Слушаем событие навигации между страницами (custom event)
    const handleNavigationChange = () => {
      fetchUnviewedCount();
    };
    
    window.addEventListener('navigation-change', handleNavigationChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('navigation-change', handleNavigationChange);
    };
  }, [fetchUnviewedCount]);

  return {
    unviewedCount,
    isLoading,
    fetchUnviewedCount,
    markAsViewed
  };
};

